/* eslint-disable max-lines */

import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { DropzoneArea } from 'mui-file-dropzone'

import { Button } from '@nexiahome/ui-common/components/primitives/buttons'
import DownloadIcon from '@mui/icons-material/DownloadOutlined'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import styled from 'styled-components'

import Api from 'services/api'
import blobToFileDownload from 'helpers/blobToFileDownload'
import { color } from 'styles/colors'
import HehmInvoiceReport from 'types/HehmInvoiceReport'
import { PageHeader, PageTitle, SinglePanePage, screenSize } from 'styles/shared'

import Spinner from 'components/Spinner'
import Tabs, { Tab } from 'components/Tabs'

import AssignFreeHehmTrial from './AssignFreeHehmTrial'
import HehmActivityReport from './HehmActivityReport'
import HehmOracle from './HehmOracle'

const TABS = [
  { key: 'free_trial', header: 'Free Trial', detailsComponent: AssignFreeHehmTrial },
  { key: 'oracle', header: 'Oracle', detailsComponent: HehmOracle },
  { key: 'activity_report', header: 'Activity Report', detailsComponent: HehmActivityReport }
]

const HehmAdmin = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get('tab')
  const initialTabIndex = Math.max(0, TABS.findIndex(t => t.key === tabParam))
  const [activeTabIndex, setActiveTabIndex] = useState(initialTabIndex)
  const [hehmInvoiceReport, setHehmInvoiceReport] = useState<HehmInvoiceReport[]>([])
  const [isLoadingReport, setIsLoadingReport] = useState(false)
  const [showCsvDropzone, setShowCsvDropzone] = useState(false)
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null)

  const fetchReport = async (silent = false) => {
    if (!silent) setIsLoadingReport(true)
    try {
      const reportData = await Api.Paywall.getDsoHehmInvoiceReport()
      setHehmInvoiceReport(reportData || [])
      setUploadedFileName(null)
    } catch (error) {
      console.error('Failed to fetch HEHM invoice report:', error)
    } finally {
      if (!silent) setIsLoadingReport(false)
    }
  }

  const handleCsvDrop = async (files: File[]) => {
    if (files.length === 0) return

    const file = files[0]
    const reader = new FileReader()

    reader.onload = async () => {
      const csvContent = reader.result as string
      setIsLoadingReport(true)
      setShowCsvDropzone(false)

      try {
        const reportData = await Api.Paywall.getDsoHehmInvoiceReportFromCsv(csvContent)
        setHehmInvoiceReport(reportData || [])
        setUploadedFileName(file.name)
      } catch (error) {
        console.error('Failed to process CSV file:', error)
      } finally {
        setIsLoadingReport(false)
      }
    }

    reader.readAsText(file)
  }

  useEffect(() => {
    fetchReport()
  }, [])

  const onTabChange = (index: number) => {
    setActiveTabIndex(index)
    setSearchParams({ tab: TABS[index].key })
    if (TABS[index].key !== 'oracle') setShowCsvDropzone(false)
  }

  const escapeCSV = (value: string) => {
    if (value == null) return ''
    const str = String(value)
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
      return `"${str.replace(/"/g, '""')}"`
    }
    return str
  }

  const generateExternalPaymentCSV = async (invoiceReport: HehmInvoiceReport[]) => {
    const headers = ['Organization Name', 'Location', 'Salesperson', 'Default Branch', 'GL Code', 'R12', 'Product SKU', 'Description', 'Amount']
    const rows = invoiceReport.flatMap(invoice => {
      const subtotalFormatted = invoice.charges.map(c => Number(c.amount)).reduce((a, b) => a + b, 0).toFixed(2)
      const commonFields = [
        escapeCSV(invoice.organizationName),
        escapeCSV(invoice.location),
        escapeCSV(invoice.salesperson),
        escapeCSV(invoice.defaultBranch),
        escapeCSV(invoice.glCode),
        escapeCSV(invoice.r12AccountNumber),
        escapeCSV(invoice.productSku)
      ]

      const subtotalRow = [...commonFields, escapeCSV('Subtotal'), subtotalFormatted].join(',')
      const taxRows = invoice.taxes.map(t =>
        [...commonFields, escapeCSV(t.description), Number(t.amount).toFixed(2)].join(',')
      )

      return [subtotalRow, ...taxRows]
    })

    const csvContent = [headers.join(','), ...rows].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    blobToFileDownload(blob, 'external_payment_report.csv')
  }

  if (isLoadingReport) {
    return <Spinner variant="page" />
  }

  return (
    <>
      <S.PageHeader>
        <S.TitleRow>
          <PageTitle>EHMKT</PageTitle>
          {uploadedFileName && <S.UploadedFileIndicator>Using: {uploadedFileName}</S.UploadedFileIndicator>}
        </S.TitleRow>
        {TABS[activeTabIndex].key === 'oracle' && (
          <S.ActionButtons>
            <S.UploadButton>
              <UploadFileIcon onClick={() => setShowCsvDropzone(!showCsvDropzone)} style={{ cursor: 'pointer', color: color('primaryButton') }} />
              <Button decoration="secondary" text={showCsvDropzone ? 'Cancel Upload' : 'Upload CSV'} onPress={() => setShowCsvDropzone(!showCsvDropzone)} />
            </S.UploadButton>
            {uploadedFileName && (
              <Button decoration="textOnly" text="Reset to Aria" onPress={() => fetchReport()} />
            )}
            <S.DownloadButton>
              <DownloadIcon onClick={() => generateExternalPaymentCSV(hehmInvoiceReport)} style={{ cursor: 'pointer', color: color('primaryButton') }} />
              <Button decoration="primary" text="Download CSV" onPress={() => generateExternalPaymentCSV(hehmInvoiceReport)} />
            </S.DownloadButton>
          </S.ActionButtons>
        )}
      </S.PageHeader>
      {showCsvDropzone && (
      <S.DropzoneContainer>
        <DropzoneArea
            fileObjects={[]}
            acceptedFiles={['.csv', 'text/csv', 'application/vnd.ms-excel']}
            filesLimit={1}
            dropzoneText="Drag and drop an Aria CSV export file here or click to select"
            onDrop={handleCsvDrop}
            showPreviewsInDropzone={false}
            maxFileSize={10000000}
        />
      </S.DropzoneContainer>
      )}
      <S.SinglePanePage>
        <Tabs
          tabpage="others"
          activeTabIndex={activeTabIndex}
          onSelectTabIndexCallback={(index) => onTabChange(index)}
          tabItems={
            TABS.map(r => {
              let details
              if (r.key === 'oracle') {
                details = <HehmOracle hehmInvoiceReport={hehmInvoiceReport} refreshCallback={() => fetchReport(true)} />
              } else if (r.key === 'activity_report') {
                details = <HehmActivityReport />
              } else {
                details = <AssignFreeHehmTrial />
              }

              const item: Tab = {
                key: r.header,
                header: <S.TabHeader>{r.header}</S.TabHeader>,
                details
              }

              return item
            })
          }
        />
      </S.SinglePanePage>
    </>
  )
}

export default HehmAdmin

const S = {
  DownloadButton: styled.div`
    display: flex;
    align-items: center;
    & button { border: none !important; }
  `,
  UploadButton: styled.div`
    display: flex;
    align-items: center;
    margin-right: 10px;
    & button { border: none !important; }
  `,
  ActionButtons: styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    * { text-transform: none !important; }
    & button { text-transform: none !important; }
  `,
  PageHeader: styled(PageHeader)`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
  `,
  TitleRow: styled.div`
    display: flex;
    align-items: center;
    gap: 15px;
  `,
  UploadedFileIndicator: styled.span`
    font-size: 12px;
    color: ${color('secondaryText')};
    background-color: ${color('tableHeaderBackground')};
    padding: 4px 8px;
    border-radius: 4px;
  `,
  DropzoneContainer: styled.div`
    margin: 20px 0;
    padding: 10px;
    background-color: ${color('tableHeaderBackground')};
    border-radius: 8px;
  `,
  TabHeader: styled.div`
    font-size: 14px;
    font-weight: bold;
    padding: 10px;
`,
  SinglePanePage: styled(SinglePanePage)`
    @media ${screenSize.small} {
      margin-top: 0;
    }
  `
}
